import React, { useCallback, useEffect, useState } from "react";
import { ManagedResourceTypeHeader } from "../headers/PageHeaders";
import { Table, Button, Tooltip, Alert } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import {
  deleteResource,
  getManagedResourceType,
  getMyResourcesInManagedResourceType,
  transferResourceOwner,
  patchResource,
  getServiceApplicationSubscriptionStatusForIdentity,
} from "../../actions/api";
import { getMyRequests } from "../../actions";
import {
  getResourceDetails,
  getResourceDisplayColumns,
} from "../common/ResourcesUtils";
import { useDispatch } from "react-redux";

const ManagedResourcesList = ({
  managedResourceTypeName,
  identityId,
  loading,
}) => {
  const dispatch = useDispatch();
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [managedResourceType, setManagedResourceType] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState();
  const [ownerFilter, setOwnerFilter] = useState();
  const [managedResourceTypeIssue, setManagedResourceTypeIssue] =
    useState(false);
  const [noData, setNoData] = useState(false);

  const fetchManagedResourcesType = useCallback(
    async (managedResourceTypeName) => {
      setIsLoading(true);
      const action = await dispatch(
        getManagedResourceType(managedResourceTypeName),
      );
      if (action && action.payload && action.payload.data)
        setManagedResourceType(action.payload.data);
      setIsLoading(false);
    },
    [dispatch],
  );

  useEffect(() => {
    fetchManagedResourcesType(managedResourceTypeName);
  }, [fetchManagedResourcesType, managedResourceTypeName]);

  const fetchManagedResourcesTypeIssue = useCallback(
    async (managedResourceType, identityId) => {
      const action = await dispatch(
        getServiceApplicationSubscriptionStatusForIdentity(
          managedResourceType.applicationSubscription.applicationId,
          identityId,
        ),
      );
      if (action?.payload?.data?.status !== "Subscribed")
        setManagedResourceTypeIssue(true);
    },
    [dispatch],
  );

  useEffect(() => {
    if (managedResourceType?.applicationSubscription?.applicationId)
      fetchManagedResourcesTypeIssue(managedResourceType, identityId);
  }, [fetchManagedResourcesTypeIssue, identityId, managedResourceType]);

  const fetchResources = useCallback(
    async (managedResourceType, filter, ownerFilter) => {
      if (managedResourceType) {
        setIsLoading(true);
        const action = await dispatch(
          getMyResourcesInManagedResourceType(
            managedResourceType.resourceTypeName,
            filter,
          ),
        );
        if (action && action.payload && action.payload.data) {
          if (ownerFilter)
            setResources(
              action.payload.data.filter((resource) =>
                resource.owner?.upn
                  ? resource.owner.upn.includes(ownerFilter)
                  : false,
              ),
            );
          else setResources(action.payload.data);

          // If there are no resources (and no filter applied)
          if (action.payload.data.length <= 0 && !filter && !ownerFilter)
            setNoData(true);
          else setNoData(false);
        }
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (managedResourceType)
      fetchResources(managedResourceType, searchCriteria, ownerFilter);
  }, [fetchResources, managedResourceType, searchCriteria, ownerFilter]);

  const onSearch = async (value) => {
    if (value && value.length >= 2) {
      setSearchCriteria({
        filter: `displayName:Contains:${value}`,
        sort: "displayName:asc",
      });
    } else {
      setSearchCriteria();
      await fetchResources(managedResourceType, undefined, ownerFilter);
    }
  };

  const onDelete = async (item) => {
    await dispatch(deleteResource(item, managedResourceType.resourceTypeName));
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onApprove = async (item) => {
    await dispatch(
      transferResourceOwner(
        managedResourceType.resourceTypeName,
        item.id,
        item.ownerId,
      ),
    );
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onOwnerChange = async (item, owner) => {
    await dispatch(
      transferResourceOwner(
        managedResourceType.resourceTypeName,
        item.id,
        owner.id,
      ),
    );
    await dispatch(getMyRequests());
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onSelectNewAdminGroup = async (item, group) => {
    const data = [
      {
        value: group?.id,
        path: "/administratorsId",
        op: "replace",
      },
    ];
    await dispatch(patchResource(item.managedResourceType, item.id, data));
    await dispatch(getMyRequests());
    await fetchResources(managedResourceType, searchCriteria, ownerFilter);
  };

  const onSearchOwner = async (value) => {
    if (!value) setOwnerFilter();
    else if (value.length >= 2) {
      setOwnerFilter(value);
      // we filter the results in state,
      // as we cannot use filter on the API without first searching for ownerId
    } else setOwnerFilter();
  };

  const resourceColumns = getResourceDisplayColumns(
    onDelete,
    onApprove,
    onOwnerChange,
    onSelectNewAdminGroup,
    identityId,
    managedResourceType.managementUrlTemplate,
    managedResourceType.accessUrlTemplate,
    false,
    onSearchOwner,
    onSearch,
  );

  const renderActionButtons = (managedResourceType) => {
    return (
      <>
        {managedResourceType.createResourceUrl && (
          <Tooltip title="Create a new Resource of this type">
            <Button
              type="default"
              href={managedResourceType.createResourceUrl}
              target="_blank"
              icon={<PlusOutlined />}
            >
              Create
            </Button>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      <ManagedResourceTypeHeader
        title={managedResourceType.resourceTypeName}
        button={renderActionButtons(managedResourceType)}
        subTitle={managedResourceType.description}
      />
      {managedResourceTypeIssue && !noData && (
        <Alert
          message="Warning: you own resources of this type but are not subscribed to the corresponding Application."
          description="Please subscribe to the Service Collection and the Application before trying actions on the resources 
            below, or please reassign them to another owner."
          type="warning"
          showIcon
          closable
        />
      )}

      <Table
        loading={isLoading || loading}
        columns={resourceColumns}
        rowKey={(record) => record.id}
        dataSource={resources}
        expandable={{
          expandedRowRender: (record) => getResourceDetails(record, false),
        }}
        showHeader={!noData}
        locale={{ emptyText: "No resources found" }}
        pagination={{ hideOnSinglePage: true }}
      />
    </>
  );
};

ManagedResourcesList.propTypes = {
  managedResourceTypeName: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  identityId: PropTypes.string,
};

export default ManagedResourcesList;
